/**
 * Things that stand in water (`PropSpec.wet`): mission 20's ammunition barge and mission 13's bridge
 * pier. StaticWorld seats a prop on the river BED, and the surface lies `WATERLINE` above a
 * mid-channel bed, so both are modelled to look right cut off at that height - and on dry ground.
 */

import type { PropKind } from '../../sim/types';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { sided, snowPatch } from './parts';
import { prism } from './shapes';
import { capColor } from './snow';

/**
 * Height of the water above the model's y = 0 in mid-channel: the bed lies 0.9 m down and the surface
 * 0.3 m (river.ts RIVER_BED, RIVER_LEVEL), and StaticWorld seats a prop 5 cm into whatever it stands on.
 * kit3World.test.ts checks the figure against the real world. Nearer a bank the water is shallower.
 */
export const WATERLINE = 0.65;

/**
 * A HULL floats; a footing stands. Both are `PropSpec.wet`, but only a hull is seated by the
 * surface: out of open water - frozen in, or on dry ground - it rests on what is there, boot-topping
 * and all, instead of being sunk to its deck. A bridge pier is founded on the bed whatever the river
 * is doing, or it would stand proud of the deck it carries (StaticWorld.seatY).
 */
export function floatsOnWater(kind: PropKind): boolean {
  return kind === 'barge';
}

type Outline = ReadonlyArray<readonly [number, number]>;
/** Barge plan, [x, z]: square stern to the north, the bow drawn in to the south. */
export const BARGE_HULL: Outline = [[-2.1, -5.9], [2.1, -5.9], [2.1, 4.0], [1.1, 5.9], [-1.1, 5.9], [-2.1, 4.0]];
export const BARGE_DECK_Y = 1.05;

/**
 * Cargo barge: long, low, red below the waterline and slate above, its hold full of crates under
 * lashed tarpaulins - one corner thrown back to show the enemy's band on an ammunition crate - with
 * a wheelhouse at the stern and mooring bollards at bow and stern.
 */
export const barge: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const boot = WATERLINE + 0.12;
  prism(mb, BARGE_HULL, 0, boot, { top: p.color('enemyAccent.0'), side: p.color('enemyAccent.0') });
  prism(mb, BARGE_HULL, boot, BARGE_DECK_Y, { top: p.color('metal.mid'), side: p.color('roof.0') });
  // Hatch coaming round the hold.
  const coaming = { all: p.color('metal.lo'), top: p.color('metal.mid') };
  for (const sz of [-3.75, 3.55]) mb.box(3.3, 0.3, 0.15, coaming, place(0, BARGE_DECK_Y, sz));
  for (const sx of [-1, 1]) mb.box(0.15, 0.3, 7.3, coaming, place(sx * 1.58, BARGE_DECK_Y, -0.1));

  // Three tarpaulined stacks of uneven height, two lashings over each.
  let z = -3.5;
  for (let i = 0; i < 3; i++) {
    const d = 2.1 + (i === 1 ? 0.3 : 0);
    const h = rng.range(0.7, 1.0);
    mb.box(2.8, h, d, sided(p, 'canvas', p.color('canvas.hi')), place(0, BARGE_DECK_Y, z + d / 2));
    const rope = p.color('timber.lo');
    for (const dz of [-0.5, 0.5]) mb.box(2.86, 0.05, 0.09, rope, place(0, BARGE_DECK_Y + h, z + d / 2 + dz));
    snowPatch(mb, p, rng.range(-0.4, 0.4), BARGE_DECK_Y + h + 0.04, z + d / 2, 1.5, 0.7);
    z += d + 0.12;
  }
  // The uncovered crate at the bow end of the hold: what the barge is carrying.
  mb.box(1.0, 0.5, 0.7, { all: p.color('timber.mid'), top: capColor(p, 'timber.hi') }, place(0.6, BARGE_DECK_Y, 3.95));
  mb.box(1.02, 0.12, 0.72, p.color('enemyAccent.0'), place(0.6, BARGE_DECK_Y + 0.2, 3.95));

  // Wheelhouse on the stern, window to the south over the cargo.
  mb.box(1.6, 1.05, 1.3, sided(p, 'timber'), place(0, BARGE_DECK_Y, -4.9));
  mb.box(1.9, 0.12, 1.6, { all: p.color('roof.0'), top: p.color('roof.1') }, place(0, BARGE_DECK_Y + 1.05, -4.9));
  mb.box(1.1, 0.4, 0.06, p.color('ink.1'), place(0, BARGE_DECK_Y + 0.5, -4.24));
  snowPatch(mb, p, 0, BARGE_DECK_Y + 1.17, -4.9, 1.2, 0.9);
  for (const sx of [-1, 1]) {
    for (const bz of [-5.45, 4.35]) {
      const x = sx * (bz > 0 ? 1.55 : 1.8);
      mb.cylinder(0.13, 0.19, 0.42, 6, p.color('metal.lo'), place(x, BARGE_DECK_Y, bz), capColor(p, 'metal.mid'));
    }
  }
  return mb;
};

/** Pier footing, [x, z]: cutwaters pointing up and down stream (the river runs east-west; the road crosses it). */
export const PIER_FOOTING: Outline = [[-1.2, 0], [-0.8, -1.1], [0.8, -1.1], [1.2, 0], [0.8, 1.1], [-0.8, 1.1]];
const swell = (o: Outline, k: number): Array<[number, number]> => o.map(([x, z]) => [x * k, z * k]);

/**
 * Top of the pier, above its y = 0 on the river bed. The bed lies 0.80-0.95 m under the banks where a pier
 * stands (shallower beside the ford a crossing carves), and the deck's planking runs from 0.15 m under the banks
 * to 0.15 m over them (pipeline.ts): so this top always lies INSIDE the planking - the deck sits on the pier, and
 * the half of the pier that shows beside the deck never stands proud of it.
 */
export const PIER_TOP = 0.9;

/**
 * Bridge pier (mission 13): what carries the deck, and drawn no higher than the deck it carries. The generator
 * stands each one under the deck's EDGE, so half of it is hidden and the other half shows beside the rail as a
 * cutwater: a stone footing with a weed line where the river stands, a pale concrete head, and on each nose a
 * demolition chamber's hatch under a band of the enemy's red - the detail that says "this is what you blow".
 * Symmetrical east to west, so it reads the same whichever edge it stands under.
 */
export const bridgePier: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  prism(mb, PIER_FOOTING, 0, 0.52, { top: p.color('rock.mid'), side: p.color('rock.lo') });
  const weed = { top: p.color('foliage.lo'), side: p.color('foliage.lo') };
  prism(mb, swell(PIER_FOOTING, 1.02), WATERLINE - 0.2, WATERLINE + 0.07, weed);
  const head = { top: p.color('concrete.hi'), side: p.color('concrete.mid') };
  prism(mb, swell(PIER_FOOTING, 0.94), 0.52, PIER_TOP, head);
  for (const sx of [-1, 1]) {
    mb.box(0.12, 0.03, 1.5, p.color('enemyAccent.0'), place(sx * 0.42, PIER_TOP, 0));
    mb.box(0.34, 0.03, 0.5, { all: p.color('metal.lo'), top: p.color('ink.1') }, place(sx * 0.74, PIER_TOP, 0));
    snowPatch(mb, p, sx * 0.62, PIER_TOP, sx * 0.55, 0.3, 0.4);
  }
  return mb;
};
